import React, { useMemo, useCallback, useRef, useState } from "react";
import { DataGrid } from "@toddle-edu/ds-data-grid";
import { EmptyState } from "@toddle-edu/ds-web";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import { useContainerWidth } from "Hooks";
import { resolveFlexHeaders } from "TranscriptsV3/modules/utils";
import classes from "./GradingPeriodsTab.scss";

const GRADING_PERIOD_NAME_COL = "name";
const DISPLAY_IN_TRANSCRIPT_COL = "displayInTranscript";
const REORDER_COL = "reorder";

const getHeaders = t => [
  {
    id: GRADING_PERIOD_NAME_COL,
    columnKey: GRADING_PERIOD_NAME_COL,
    title: t("common:grading_period_plural"),
    flex: 1,
  },
  {
    id: DISPLAY_IN_TRANSCRIPT_COL,
    columnKey: DISPLAY_IN_TRANSCRIPT_COL,
    title: t("transcript:display_in_transcript"),
    width: 200,
  },
  {
    id: REORDER_COL,
    columnKey: REORDER_COL,
    title: "",
    width: 104,
  },
];

const getGridData = ({ periods, onRowsReorder }) =>
  _.map(periods, (period, index) => {
    const isFirst = index === 0;
    const isLast = index === periods.length - 1;

    const handleMoveUp = () => {
      onRowsReorder(index, index - 1);
    };
    const handleMoveDown = () => {
      onRowsReorder(index, index + 1);
    };

    return {
      rowId: period.gradingPeriodId,
      columns: [
        {
          value: period.name || "",
          cellType: "label",
          isEditable: false,
          colId: GRADING_PERIOD_NAME_COL,
        },
        {
          value: period.isEnabled ? "checked" : "unchecked",
          cellType: "checkbox",
          isEditable: true,
          colId: DISPLAY_IN_TRANSCRIPT_COL,
        },
        {
          cellType: "icon",
          colId: REORDER_COL,
          value: [
            {
              name: "UpArrowOutlined",
              isDisabled: isFirst,
              onIconClick: isFirst ? () => {} : handleMoveUp,
            },
            {
              name: "DownArrowOutlined",
              isDisabled: isLast,
              onIconClick: isLast ? () => {} : handleMoveDown,
            },
          ],
        },
      ],
    };
  });

const GradingPeriodsTab = ({ periods, gradeMapId, onToggle, onReorder }) => {
  const { t } = useTranslation(["common", "transcript"]);
  const containerRef = useRef(null);
  const containerWidth = useContainerWidth({ containerRef });
  const [focusCell, setFocusCell] = useState({
    rowId: null,
    colId: null,
  });

  const headers = useMemo(
    () => resolveFlexHeaders({ headers: getHeaders(t), containerWidth }),
    [containerWidth, t]
  );

  const handleRowsReorder = useCallback(
    (startIdx, endIdx) => {
      const rowId = _.get(periods, [startIdx, "gradingPeriodId"]);
      if (!rowId || endIdx < 0 || endIdx >= periods.length) return;

      const reorderedPeriods = [...periods];
      const [item] = reorderedPeriods.splice(startIdx, 1);
      reorderedPeriods.splice(endIdx, 0, item);
      onReorder({ gradeMapId, reorderedPeriods });

      setFocusCell({
        rowId,
        colId: REORDER_COL,
      });

      setTimeout(() => {
        setFocusCell({
          rowId: null,
          colId: null,
        });
      });
    },
    [periods, gradeMapId, onReorder]
  );

  const gridData = useMemo(
    () => getGridData({ periods, onRowsReorder: handleRowsReorder }),
    [periods, handleRowsReorder]
  );

  const handleCellEdit = useCallback(
    cells => {
      _.forEach(_.castArray(cells), cell => {
        const { cellCoods, newValue } = cell;
        if (!cellCoods || !newValue) return;

        const colId =
          _.get(newValue, "colId") ||
          _.get(cellCoods, "colId") ||
          _.get(cellCoods, "col");
        const { rowId } = cellCoods;
        if (colId !== DISPLAY_IN_TRANSCRIPT_COL) return;

        const isEnabled = _.get(newValue, "value") === "checked";
        onToggle({ gradeMapId, gradingPeriodId: rowId, isEnabled });
      });
    },
    [gradeMapId, onToggle]
  );

  return (
    <div className={classes.container}>
      {_.isEmpty(periods) ? (
        <div className={classes.emptyStateWrapper}>
          <EmptyState
            title={t("common:grading_period_plural")}
            subtitle={t("transcript:no_grading_periods_selected")}
          />
        </div>
      ) : (
        <div ref={containerRef} className={classes.gridWrapper}>
          <DataGrid
            data={gridData}
            headers={headers}
            onCellEdit={handleCellEdit}
            focusCell={focusCell}
            rowKey="rowId"
            rowMarkers="none"
            dataGridHeight="100%"
            dataGridWidth="100%"
            columnsResizable={false}
            columnSelectionEnabled={false}
            showHoverState={false}
            tooltipDelayTime={100}
          />
        </div>
      )}
    </div>
  );
};

export default GradingPeriodsTab;
